import { useState } from 'react';
import { Link } from 'react-router-dom';
import logoPurple2 from 'shared/images/logoPurpleBlackBackground2.png';
import { SUPPORT_EMAIL } from 'shared/lib';
import { ConfigProvider, Dropdown, Layout, Menu, Popover, Tag } from 'antd';
import {
  CustomerServiceOutlined,
  DownOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useCurrentUserDetails } from 'entities/user/model';
import LogoutButton from 'widgets/Authentication/LogoutButton';

const { Sider } = Layout;

export const SIDEBAR_WIDTH = 240;
export const SIDEBAR_COLLAPSED_WIDTH = 80;

// Matches the dark shell the rest of the app already uses.
const COLORS = {
  bg: '#111827',
  border: '#1b2431',
  text: '#9ca3af',
  activeBg: '#1f2937',
  activeText: '#ffffff',
};

const renderLabel = (item) => (
  <span className="flex items-center">
    {item.name}
    {!!item.notificationBadge && <item.notificationBadge />}
  </span>
);

// The nav shape is owned by the routes; map it onto what antd's Menu expects.
const toMenuItems = (navigation) =>
  navigation.map((item) =>
    item.children
      ? {
          key: item.name,
          icon: <item.icon className="h-5 w-5" aria-hidden="true" />,
          label: renderLabel(item),
          children: item.children.map((subItem) => ({
            key: subItem.href,
            label: <Link to={subItem.href}>{renderLabel(subItem)}</Link>,
          })),
        }
      : {
          key: item.href,
          icon: <item.icon className="h-5 w-5" aria-hidden="true" />,
          label: <Link to={item.href}>{renderLabel(item)}</Link>,
        }
  );

const findSelectedKeys = (navigation) =>
  navigation.flatMap((item) =>
    item.children
      ? item.children.filter((s) => s.current).map((s) => s.href)
      : item.current
        ? [item.href]
        : []
  );

const findOpenKeys = (navigation) =>
  navigation
    .filter((item) => item.children?.some((s) => s.current))
    .map((item) => item.name);

export const Sidebar = ({
  navigation,
  userNavigation = [],
  collapsed = false,
  onCollapse,
}) => {
  const { data, currentOrganization, hasLoggedInUser, getCurrentOrgRole } =
    useCurrentUserDetails();
  const [openKeys, setOpenKeys] = useState(() => findOpenKeys(navigation));

  const userMenuItems = userNavigation.map((item) => ({
    key: item.name,
    label:
      item.name === 'Sign out' ? (
        <LogoutButton className="w-auto" />
      ) : (
        <Link to={item.href}>{item.name}</Link>
      ),
  }));

  return (
    <Sider
      theme="dark"
      trigger={null}
      collapsible
      collapsed={collapsed}
      width={SIDEBAR_WIDTH}
      collapsedWidth={SIDEBAR_COLLAPSED_WIDTH}
      className="[&>.ant-layout-sider-children]:h-full"
      style={{
        background: COLORS.bg,
        borderRight: `1px solid ${COLORS.border}`,
        height: '100vh',
      }}>
      <div className="flex flex-col" style={{ height: '100%' }}>
        {collapsed ? (
          <div className="shrink-0 pt-4" />
        ) : (
          <div className="flex shrink-0 items-center justify-center overflow-hidden px-4 pb-5 pt-7">
            <img
              className="h-7 w-auto flex-shrink-0"
              src={logoPurple2}
              alt="Fractal"
            />
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto">
          <ConfigProvider
            theme={{
            components: {
              Menu: {
                darkItemBg: 'transparent',
                darkSubMenuItemBg: 'transparent',
                darkItemColor: COLORS.text,
                darkItemHoverBg: COLORS.activeBg,
                darkItemHoverColor: COLORS.activeText,
                darkItemSelectedBg: COLORS.activeBg,
                darkItemSelectedColor: COLORS.activeText,
              },
            },
          }}>
            <Menu
              theme="dark"
              mode="inline"
              inlineCollapsed={collapsed}
              items={toMenuItems(navigation)}
              selectedKeys={findSelectedKeys(navigation)}
              openKeys={collapsed ? undefined : openKeys}
              onOpenChange={setOpenKeys}
              className="!border-e-0 !bg-transparent"
            />
          </ConfigProvider>
        </div>

        <div className="mt-auto shrink-0">
          <Popover
            placement="rightBottom"
            trigger="click"
            title="Contact support"
            content={
              <div className="flex max-w-[220px] flex-col gap-2">
                <span className="text-sm text-gray-500">
                  Something not working, or a question about your account? Get
                  in touch and we will help.
                </span>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-sm font-medium text-indigo-500 hover:underline">
                  {SUPPORT_EMAIL}
                </a>
              </div>
            }>
            <button
              type="button"
              className={`flex w-full cursor-pointer items-center gap-x-3 px-6 py-3 text-sm text-gray-400 hover:bg-gray-800 hover:text-white ${
                collapsed ? 'justify-center px-0' : ''
              }`}>
              <CustomerServiceOutlined style={{ fontSize: 18 }} />
              {!collapsed && 'Support'}
            </button>
          </Popover>

          {hasLoggedInUser && (
            <Dropdown
              menu={{ items: userMenuItems }}
              trigger={['click']}
              placement="topRight">
              <div
                className={`flex cursor-pointer items-center gap-x-3 border-t px-4 py-3 hover:bg-gray-800 ${
                  collapsed ? 'justify-center px-0' : ''
                }`}
                style={{ borderColor: COLORS.border }}>
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-slate-700 text-slate-200">
                  <UserOutlined />
                </span>
                {!collapsed && (
                  <>
                    <span className="flex min-w-0 flex-1 flex-col gap-y-0.5">
                      <span className="flex items-center gap-x-2">
                        <span className="truncate text-sm font-semibold capitalize leading-5 text-white">
                          {data.nickname}
                        </span>
                        <Tag className="!mr-0 !border-slate-600 !bg-slate-700 !text-[10px] !uppercase !leading-4 !text-indigo-300">
                          {getCurrentOrgRole()}
                        </Tag>
                      </span>
                      {!!currentOrganization?.name && (
                        <span className="truncate text-xs leading-4 text-slate-300">
                          {currentOrganization.name}
                        </span>
                      )}
                      {!!data.email && (
                        <span className="truncate text-xs leading-4 text-gray-500">
                          {data.email}
                        </span>
                      )}
                    </span>
                    <DownOutlined style={{ fontSize: 11, color: COLORS.text }} />
                  </>
                )}
              </div>
            </Dropdown>
          )}

          {!!onCollapse && (
            <div
              className={`flex border-t px-4 py-2 ${
                collapsed ? 'justify-center' : 'justify-end'
              }`}
              style={{ borderColor: COLORS.border }}>
              <button
                type="button"
                aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
                onClick={() => onCollapse(!collapsed)}
                className="rounded-md p-2 text-gray-400 hover:bg-gray-800 hover:text-white">
                {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              </button>
            </div>
          )}
        </div>
      </div>
    </Sider>
  );
};
